/**
 * LineJoinModal.jsx
 *
 * Put a broken utterance back together — the counterpart to LineSplitModal.
 *
 * ⛔ WHY IT EXISTS. The PDF→HTML converter ends a paragraph at every font change, so one italicised
 * word breaks an utterance into three rows and loses its type and its speaker with it. Reassigning
 * each row was rendered and rejected by ear: *"not smooth and listens erratically in a stitched
 * .mp3"* — consecutive same-speaker rows take a 250 ms pause, and a one-word fragment rendered alone
 * gets sentence-final intonation rather than the stress the italics were asking for. Full case:
 * docs/journal/A_FONT_CHANGE_BROKE_THE_SENTENCE.md.
 *
 * ⭐ ONE ACTION FIXES ALL THREE INJURIES. The joined line carries its own type and speaker, so this
 * dialog repairs the fragmentation, the type corruption and the speaker loss together — where
 * reassigning row by row fixes only the speaker and leaves the other two.
 *
 * ⚠️ NO CONFIRMATION STEP, for the same reason the split's cut-point picker needed none: the server
 * proves the joined text equals the rows it replaces, word for word, before it writes anything. A
 * control that cannot lose text does not need to ask. What it DOES need to show is the result, since
 * the operator is choosing a reading — hence the preview.
 *
 * ⛔ THE ANCHOR IS THE FIRST ROW, and the server enforces contiguity: rows must sit next to each
 * other, or joining them would move whatever sits between. This dialog therefore refuses to open on
 * a non-contiguous selection rather than letting the server reject it after the fact.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Icon, Message, Modal, Segment } from "semantic-ui-react";
import { optionsFor } from "./LineEmotionPicker";

const KEEP = "__keep__";

/** The joined text, exactly as the server will compute and verify it. */
const joinText = (rows) => rows.map((r) => String(r.text || "").trim()).filter(Boolean).join(" ");

/**
 * ⭐ A SENSIBLE DEFAULT SPEAKER, because the fragments are usually unattributed and the remainder is
 * usually the Narrator — so neither is the answer. The first row that names somebody other than the
 * Narrator is almost always the character whose line this is.
 */
const guessSpeaker = (rows) => {
  const named = rows
    .map((r) => String(r.speaker || "").trim())
    .filter((s) => s && s.toLowerCase() !== "narrator");
  return named[0] || null;
};

/**
 * ⭐ THE JOINED LINE IS THE FIRST POINT AT WHICH AN EMOTION IS CLASSIFIABLE.
 *
 * ⛔ The fragments were never classified, and could not have been: the classifier saw clauses. Marva's
 * run, measured 2026-10-03 — anchor `"So who says there's only one"` emotion **null**, `"last"` null,
 * and only the long tail carrying `"neutral"`, which is the default rather than a judgement. The part
 * inherits the anchor (`p.emotion || row.emotion`), so a join of fragments inherits nothing and renders
 * on the neutral preset — which this corpus has measured as INERT.
 *
 * So the dialog asks. It defaults to the longest absorbed row's emotion if any row has one, because that
 * is the only fragment long enough for the classification to have meant anything.
 */
const guessEmotion = (rows) => {
  const withEmotion = rows.filter((r) => r.emotion && String(r.emotion).toLowerCase() !== 'neutral');
  if (!withEmotion.length) return null;
  return withEmotion.sort((a, b) => String(b.text || '').length - String(a.text || '').length)[0].emotion;
};

const LineJoinModal = ({ open, rows, roster, emotionVocabulary, busy, error, onClose, onJoin }) => {
  const ordered = useMemo(
    // Already in reading order from the table; sorted on _pos so the dialog is order-independent.
    () => [...(rows || [])].sort((a, b) => (a._pos ?? 0) - (b._pos ?? 0)),
    [rows],
  );
  const [speaker, setSpeaker] = useState(KEEP);
  const [type, setType] = useState("dialogue");
  const [emotion, setEmotion] = useState(KEEP);

  // Re-seed whenever the selection changes, so reopening on a different run does not inherit the
  // last run's answers.
  useEffect(() => {
    if (!open) return;
    setSpeaker(guessSpeaker(ordered) || KEEP);
    setType("dialogue");
    setEmotion(guessEmotion(ordered) || KEEP);
  }, [open, ordered]);

  const emotionOptions = useMemo(() => optionsFor(emotionVocabulary), [emotionVocabulary]);
  const text = joinText(ordered);

  /**
   * ⛔ CONTIGUITY, CHECKED HERE TOO. The server is the authority — it is the only layer that can see
   * the whole book — but a dialog that opens and then fails is worse than one that explains why it
   * cannot. `_pos` — the row's position in the table the operator is looking at — is enough: rows
   * adjacent there are adjacent in the book. ⚠️ Deliberately NOT `lineIndex`, which the line-set
   * projection does not carry, so a check written against it would silently never fire.
   */
  const gap = useMemo(() => {
    for (let i = 1; i < ordered.length; i += 1) {
      const a = ordered[i - 1]._pos;
      const b = ordered[i]._pos;
      if (a == null || b == null) return null;      // cannot tell — let the server decide
      if (b !== a + 1) return { from: a, to: b };
    }
    return null;
  }, [ordered]);

  const alreadyEdited = ordered.filter((r) => r.editPart || r.replacedAt);
  const canJoin = ordered.length >= 2 && !gap && !alreadyEdited.length && !busy;

  const options = [
    { key: KEEP, value: KEEP, text: "— keep the first row's speaker —" },
    ...(roster || []).map((c) => ({
      key: c.uuid || c.name, value: c.name, text: c.name,
      description: c.dialogueLineCount != null ? `${c.dialogueLineCount} lines` : undefined,
    })),
  ];

  return (
    <Modal open={!!open} onClose={onClose} size="small" closeIcon={!busy}>
      <Modal.Header>
        <Icon name="compress" /> Join {ordered.length} rows into one line
      </Modal.Header>
      <Modal.Content>
        <p style={{ marginBottom: ".6em" }}>
          These rows are one utterance that the import broke apart. Joining them renders them as a
          single take, so there is no pause and no restart in the middle of the sentence.
        </p>

        {/* The rows being consumed, in reading order — the operator is approving an order as much as
            a text, so the order is shown rather than implied. */}
        <Segment secondary style={{ padding: ".6em .8em" }}>
          {ordered.map((r, i) => (
            <div key={r.lineId} style={{ fontSize: "13px", marginBottom: i === ordered.length - 1 ? 0 : ".4em" }}>
              <span style={{ opacity: 0.6 }}>
                {i === 0 ? "keeps the line" : "absorbed"} · {r.type || "—"} · {r.speaker || "no speaker"}
              </span>
              <div>{String(r.text || "").slice(0, 200)}</div>
            </div>
          ))}
        </Segment>

        {gap && (
          <Message negative size="small">
            <Icon name="ban" /> These rows are not next to each other — there is a gap between{" "}
            {gap.from} and {gap.to}. Joining across it would move whatever sits in between. Tick the
            rows in one unbroken run.
          </Message>
        )}
        {!!alreadyEdited.length && (
          <Message negative size="small">
            <Icon name="ban" /> {alreadyEdited.length} of these rows has already been split or joined.
            Undo that edit first — one row, one editorial decision.
          </Message>
        )}

        <div style={{ margin: ".8em 0 .4em", fontWeight: 600 }}>Reads as one line:</div>
        <Segment style={{ padding: ".6em .8em", fontSize: "14px", lineHeight: 1.5 }}>
          {text || <span style={{ opacity: 0.6 }}>nothing to join</span>}
        </Segment>

        <div style={{ display: "flex", gap: "1em", marginTop: ".8em", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: "12px", opacity: 0.75, marginBottom: ".2em" }}>Spoken by</div>
            <Dropdown
              selection
              search
              value={speaker}
              options={options}
              onChange={(_, d) => setSpeaker(d.value)}
              disabled={busy}
              style={{ minWidth: "16em" }}
            />
          </div>
          {/* ⭐ Only when the vocabulary is loaded — absent, the Emotion column on the row is the
              read-only chip it has always been, and this degrades to the prior behaviour. */}
          {/* ⚠️ `emotionVocabulary` is an OBJECT — `{ labels: [...] }` — not an array. A first draft
              tested `.length` on it, which is undefined, so the control would have silently never
              rendered. `optionsFor` is the shared builder and it also shows each label's audioTag,
              which is the only place the operator can see what will actually be sent. */}
          {!!emotionOptions.length && (
            <div>
              <div style={{ fontSize: "12px", opacity: 0.75, marginBottom: ".2em" }}>Delivered as</div>
              <Dropdown
                selection
                search
                value={emotion}
                options={[{ key: KEEP, value: KEEP, text: "— no emotion (neutral) —" }, ...emotionOptions]}
                onChange={(_, d) => setEmotion(d.value)}
                disabled={busy}
                style={{ minWidth: "13em" }}
              />
            </div>
          )}
          <div>
            <div style={{ fontSize: "12px", opacity: 0.75, marginBottom: ".2em" }}>As</div>
            <Dropdown
              selection
              value={type}
              options={[
                { key: "dialogue", value: "dialogue", text: "Dialogue" },
                { key: "narration", value: "narration", text: "Narration" },
              ]}
              onChange={(_, d) => setType(d.value)}
              disabled={busy}
              style={{ minWidth: "11em" }}
            />
          </div>
        </div>
        {/* ⚠️ Named explicitly, because it is the half of this dialog that is easy to miss and the
            reason a join beats three reassignments. */}
        <div style={{ fontSize: "12px", opacity: 0.75, marginTop: ".5em" }}>
          The import filed these fragments as quoted text with no speaker, and never classified their
          emotion — the classifier saw clauses. Setting these here fixes the type, the attribution and the
          delivery at the same time as the break.
        </div>

        {error && <Message negative size="small" style={{ marginTop: ".8em" }}><Icon name="warning sign" /> {String(error)}</Message>}
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={onClose} disabled={!!busy}>Cancel</Button>
        <Button
          primary
          loading={!!busy}
          disabled={!canJoin}
          onClick={() => onJoin({
            anchorLineId: ordered[0].lineId,
            kind: "join",
            // ⚠️ The server reads these rows' text from the graph rather than trusting the client, so
            // only the ids travel. The server re-derives reading order from lineIndex (which it
            // can see) and enforces contiguity authoritatively.
            absorbs: ordered.slice(1).map((r) => r.lineId),
            parts: [{
              type,
              speakerRaw: speaker === KEEP ? null : speaker,
              // null = inherit the anchor, which for a fragment run means no emotion at all.
              emotion: emotion === KEEP ? null : emotion,
              content: text,
            }],
          })}
        >
          <Icon name="compress" /> Join into one line
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default LineJoinModal;
